import React, { useRef, useState } from "react";
import { Camera, ImagePlus, X, Loader2 } from "lucide-react";
import { useT } from "../lib/i18n";

// Reusable mandatory-photo uploader: camera capture (mobile) + gallery, multi-photo,
// thumbnail preview + remove. `uploadFile(file) -> token` resolves the stored identifier
// (a disk path for prep, or a base64 data URL for incidents). `Thumb` renders a token.
export function PhotoEvidence({
  tokens = [], setTokens, uploadFile, Thumb, max = 6,
  label, hint, testid = "photo-evidence", disabled,
}) {
  const { t } = useT();
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    const arr = Array.from(files || []).slice(0, max - tokens.length);
    if (!arr.length) return;
    setBusy(true);
    try {
      const added = [];
      for (const f of arr) {
        try { const tok = await uploadFile(f); if (tok) added.push(tok); } catch { /* skip bad file */ }
      }
      if (added.length) setTokens([...tokens, ...added]);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={testid}>
      {label && (
        <p className="mb-1.5 text-xs font-semibold text-ink-soft">
          {label}{tokens.length > 0 && <span className="text-emerald-600"> · {tokens.length}</span>}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {tokens.map((tok, i) => (
          <div key={i} className="relative h-20 w-20 overflow-hidden rounded-xl border border-line bg-app" data-testid={`${testid}-thumb-${i}`}>
            {Thumb ? <Thumb token={tok} /> : <img src={tok} alt="" className="h-full w-full object-cover" />}
            {!disabled && (
              <button type="button" onClick={() => setTokens(tokens.filter((_, j) => j !== i))}
                data-testid={`${testid}-remove-${i}`}
                className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80">
                <X size={12} />
              </button>
            )}
          </div>
        ))}
        {!disabled && tokens.length < max && (
          <>
            <button type="button" onClick={() => camRef.current?.click()} disabled={busy} data-testid={`${testid}-camera`}
              className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-line text-ink-mute transition hover:border-primary hover:text-primary disabled:opacity-50">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
              <span className="text-[10px] font-semibold">{t("Camera")}</span>
            </button>
            <button type="button" onClick={() => galRef.current?.click()} disabled={busy} data-testid={`${testid}-gallery`}
              className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-line text-ink-mute transition hover:border-primary hover:text-primary disabled:opacity-50">
              <ImagePlus size={18} />
              <span className="text-[10px] font-semibold">{t("Gallery")}</span>
            </button>
          </>
        )}
      </div>
      {hint && <p className="mt-1 text-[10px] text-ink-mute">{hint}</p>}
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
      <input ref={galRef} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
    </div>
  );
}
